/**
 * Сортировка групп работ: group_order_num → ADD_WORK после родителя → имя.
 * @param {object} a
 * @param {object} b
 */
export function compareSmetaGroupSort(a, b) {
  const groupOrderA = Number(a.group_order_num ?? 9999);
  const groupOrderB = Number(b.group_order_num ?? 9999);
  if (groupOrderA !== groupOrderB) return groupOrderA - groupOrderB;

  const isAddWorkA = a.source_work_set_check_group_id != null ? 1 : 0;
  const isAddWorkB = b.source_work_set_check_group_id != null ? 1 : 0;
  if (isAddWorkA !== isAddWorkB) return isAddWorkA - isAddWorkB;

  const groupNameA = String(a.work_set_check_group_name ?? a.label ?? "");
  const groupNameB = String(b.work_set_check_group_name ?? b.label ?? "");
  return groupNameA.localeCompare(groupNameB, "ru");
}

/** Сумма без НДС: из API или запасной вариант — сумма с НДС. */
function amountWithoutNds(withNds, withoutNds) {
  if (withoutNds != null && withoutNds !== "") {
    const n = Number(withoutNds);
    if (!Number.isNaN(n)) return n;
  }
  const n = Number(withNds);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Преобразует сырые строки smeta_new_report в строки таблицы (как ProjectSmetaNew).
 * @param {Array<object>} rows
 * @param {{ withProjectKey?: boolean }} options — для сметы по ЖК нескольких проектов в ключ включается project_id
 */
export function buildSmetaNewTableRows(rows, options = {}) {
  const { withProjectKey = false } = options;
  if (!rows?.length) return [];

  const workKey = (row) => {
    const p =
      withProjectKey && row.project_id != null && row.project_id !== ""
        ? `${row.project_id}_`
        : "";
    const pe =
      withProjectKey &&
      row.project_entrance_id != null &&
      row.project_entrance_id !== ""
        ? `${row.project_entrance_id}_`
        : "";
    return `${p}${pe}${row.entrance || ""}_${row.level_num ?? ""}_${row.work_set_check_group_id ?? ""}_${row.work_set_id || ""}`;
  };

  const groupHeaderKey = (row) => {
    const p =
      withProjectKey && row.project_id != null && row.project_id !== ""
        ? `${row.project_id}_`
        : "";
    const pe =
      withProjectKey &&
      row.project_entrance_id != null &&
      row.project_entrance_id !== ""
        ? `${row.project_entrance_id}_`
        : "";
    return `${p}${pe}${row.entrance || ""}_${row.level_num ?? ""}_${row.work_set_check_group_id ?? ""}`;
  };

  const workAgg = new Map();

  rows.forEach((row) => {
    const key = workKey(row);
    let g = workAgg.get(key);

    if (!g) {
      g = {
        entrance: row.entrance,
        level_num: row.level_num,
        work_set_id: row.work_set_id,
        work_set_check_group_id: row.work_set_check_group_id,
        work_set_check_group_name: row.work_set_check_group_name,
        group_order_num: row.group_order_num,
        work_set_name: row.work_set_name,
        work_set_unit_name: row.work_set_unit_name,
        total_work_amount: Number(row.total_work_amount || 0),
        total_work_price: Number(row.total_work_price || 0),
        total_work_sum: Number(row.total_work_sum || 0),
        total_work_price_without_nds: amountWithoutNds(
          row.total_work_price,
          row.total_work_price_without_nds
        ),
        total_work_sum_without_nds: amountWithoutNds(
          row.total_work_sum,
          row.total_work_sum_without_nds
        ),
        total_material_sum: 0,
        total_material_sum_without_nds: 0,
        is_include_nds_work_set: row.is_include_nds_work_set,
        nds_percent_work_set: row.nds_percent_work_set,
        avr_count: Number(row.avr_count || 0),
        avr_call_sum: Number(row.avr_call_sum || 0),
        has_foreman_call: row.has_foreman_call || false,
        foreman_call_date: row.foreman_call_date || null,
        is_foreman_checked: row.is_foreman_checked || false,
        has_okk_call: row.has_okk_call || false,
        okk_call_date: row.okk_call_date || null,
        is_okk_checked: row.is_okk_checked || false,
        paid_total: Number(row.paid_total || 0),
        rowsCount: 0,
      };
      workAgg.set(key, g);
    }

    g.total_material_sum += Number(row.total_material_sum || 0);
    g.total_material_sum_without_nds += amountWithoutNds(
      row.total_material_sum,
      row.total_material_sum_without_nds
    );
    g.rowsCount += 1;
  });

  for (const g of workAgg.values()) {
    g.grand_total_sum =
      Number(g.total_work_sum || 0) + Number(g.total_material_sum || 0);
    g.grand_total_sum_without_nds =
      Number(g.total_work_sum_without_nds || 0) + Number(g.total_material_sum_without_nds || 0);
    g._seen = false;
  }

  const sorted = [...rows].sort((a, b) => {
    if (withProjectKey) {
      const typeCmp = String(a.project_type_name || "").localeCompare(
        String(b.project_type_name || ""),
        "ru"
      );
      if (typeCmp !== 0) return typeCmp;
      const pidA = Number(a.project_id || 0);
      const pidB = Number(b.project_id || 0);
      if (pidA !== pidB) return pidA - pidB;
      const peA = Number(a.project_entrance_id || 0);
      const peB = Number(b.project_entrance_id || 0);
      if (peA !== peB) return peA - peB;
    }

    const entranceA = Number(a.entrance || 0);
    const entranceB = Number(b.entrance || 0);
    if (entranceA !== entranceB) return entranceA - entranceB;

    const levelA = Number(a.level_num ?? 0);
    const levelB = Number(b.level_num ?? 0);
    if (levelA !== levelB) return levelA - levelB;

    const groupCmp = compareSmetaGroupSort(a, b);
    if (groupCmp !== 0) return groupCmp;

    const wnameA = String(a.work_set_name || "");
    const wnameB = String(b.work_set_name || "");
    if (wnameA !== wnameB) return wnameA.localeCompare(wnameB, "ru");

    const mnameA = String(a.material_name || "");
    const mnameB = String(b.material_name || "");
    return mnameA.localeCompare(mnameB, "ru");
  });

  let index = 1;
  let lastGroupKey = null;
  const result = [];

  sorted.forEach((item) => {
    const key = workKey(item);
    const g = workAgg.get(key);
    const isFirst = !g._seen;
    if (isFirst) {
      g._seen = true;
      g.index = index++;
    }

    const gKey = groupHeaderKey(item);
    if (isFirst && gKey !== lastGroupKey) {
      lastGroupKey = gKey;
      result.push({
        isGroupHeader: true,
        groupKey: gKey,
        work_set_check_group_id: item.work_set_check_group_id,
        work_set_check_group_name: item.work_set_check_group_name,
        entrance: item.entrance,
        level_num: item.level_num,
        floor_map_id: item.floor_map_id,
        project_id: item.project_id,
        project_name: item.project_name,
        project_type_id: item.project_type_id,
        project_type_name: item.project_type_name,
        resident_id: item.resident_id,
        resident_name: item.resident_name,
        project_entrance_id: item.project_entrance_id,
        block_name: item.block_name,
      });
    }

    result.push({
      ...item,
      index: g.index,
      groupKey: key,
      work_rowspan: isFirst ? g.rowsCount : 0,
      wname: isFirst ? g.work_set_name : "",
      wunt: isFirst ? g.work_set_unit_name : "",
      wqty: isFirst ? g.total_work_amount : null,
      wprc: isFirst ? g.total_work_price : null,
      wsum: isFirst ? g.total_work_sum : null,
      wprc_no_nds: isFirst ? g.total_work_price_without_nds : null,
      wsum_no_nds: isFirst ? g.total_work_sum_without_nds : null,
      mname: item.material_name,
      munt: item.material_unit_name,
      munc: item.material_unit_code || "",
      mqty: item.total_material_amount,
      mprc: item.total_material_price,
      msum: item.total_material_sum,
      mprc_no_nds: amountWithoutNds(
        item.total_material_price,
        item.total_material_price_without_nds
      ),
      msum_line_no_nds: amountWithoutNds(
        item.total_material_sum,
        item.total_material_sum_without_nds
      ),
      msum_no_nds: isFirst ? g.total_material_sum_without_nds : null,
      has_m19_allocation: !!item.has_m19_allocation,
      m19_allocated_qty: Number(item.m19_allocated_qty || 0),
      m19_min_qty: Number(item.m19_min_qty || item.m19_allocated_qty || 0),
      orig_wqty: g.total_work_amount,
      norm_ratio: g.total_work_amount > 0 ? item.total_material_amount / g.total_work_amount : 0,
      ttl: isFirst ? g.grand_total_sum : null,
      ttl_no_nds: isFirst ? g.grand_total_sum_without_nds : null,
      is_incloude_nds_material: item.is_include_nds_material,
      nds_percent_material: item.nds_percent_material,
      is_incloude_nds_work_set: isFirst ? g.is_include_nds_work_set : 0,
      nds_percent_work_set: isFirst ? g.nds_percent_work_set : null,
      avr_count: isFirst ? g.avr_count : null,
      avr_call_sum: isFirst ? g.avr_call_sum : null,
      has_foreman_call: isFirst ? g.has_foreman_call : null,
      foreman_call_date: isFirst ? g.foreman_call_date : null,
      is_foreman_checked: isFirst ? g.is_foreman_checked : null,
      has_okk_call: isFirst ? g.has_okk_call : null,
      okk_call_date: isFirst ? g.okk_call_date : null,
      is_okk_checked: isFirst ? g.is_okk_checked : null,
      paid_total: isFirst ? g.paid_total : null,
    });
  });

  return result;
}
